import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import { Moon, ChevronRight, PlayCircle } from "lucide-react";
import { wednesdayNavItems } from "@/pages/WednesdayNights";
import { getChapterInfo } from "@/lib/grudem";

export default function WednesdayArchive({ user }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const navItems = wednesdayNavItems(navigate, user, "archive");

  useEffect(() => {
    axios.get(`${API}/wednesday`).then((res) => setSessions(res.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
      <MobileTopBar brandLabel="Wednesday Nights" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Past Weeks</h1>
            <p className="text-sm text-slate-500 mt-0.5">Revisit any previous Wednesday night teaching and discussion.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
              <Moon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No sessions yet</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="wednesday-archive-list">
              {sessions.map((s) => {
                const ch = s.chapter_number ? getChapterInfo(s.chapter_number) : null;
                return (
                  <button
                    key={s.session_id}
                    data-testid={`wednesday-archive-item-${s.session_id}`}
                    onClick={() => navigate(`/wednesday?session_id=${s.session_id}`)}
                    className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3 text-left hover:border-indigo-300 hover:shadow-sm transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-indigo-600 leading-none text-center">
                        Wk<br />{s.week_number}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-800 truncate">{s.topic}</p>
                        {s.youtube_url && <PlayCircle className="w-3.5 h-3.5 text-indigo-400 shrink-0" />}
                        {!s.is_published && (
                          <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Draft</Badge>
                        )}
                      </div>
                      {ch && <p className="text-xs text-indigo-600 mt-0.5">Chapter {ch.n}: {ch.title}</p>}
                      {s.session_date && <p className="text-xs text-slate-400 mt-0.5">{s.session_date}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
